import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import queryString from 'query-string';
import { compose } from 'ramda';
import { FormattedHTMLMessage } from 'components/IntlProvider';
import {
  showToast,
  Button,
  Typography,
  Tables,
  Checkbox,
} from '@deeptrue-frontend/ui';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';
import { useFormatMessage } from 'components/IntlProvider';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';
import AnimatedPlaceholder from 'components/AnimatedPlaceholder/AnimatedPlaceholder';
import VoucherInfo from '../VoucherInfo';

const { connect, selectors } = StoreProvider;
const { Table, TableHeader, TableCell, TableRow, TableBody } = Tables;

interface InviteRepondentProps
  extends RouteComponentProps<{
    uuid: string;
    id: string;
    codename: string;
  }> {
  organization: OrganizationType;
  params: any;
  surveyUiid: string;
  updateProject: () => void;
  credits: number;
  templateReminder: any;
  templateEmail: any;
  projectId: string;
  hasDataCollectionStarted: boolean;
  closeModal: () => void;
  voucherInfo: any;
}

type Params = {
  [P in 'limit' | 'offset' | 'search' | 'status' | 'ordering']?: string;
};

enum sortOptions {
  created_at = 'Created at',
  updated_at = 'Updated at',
  status = 'Status',
  product_slug = 'Product slug',
  name = 'Name',
}

const Title = styled(Typography)`
  position: absolute;
  top: 2rem;
  font-weight: 500;
  left: 2.25rem;
  font-size: 1.25rem;
`;

const SubHeader = styled(Typography)`
  color: ${({ theme }) => theme.colors.tuatara};
  margin-top: 1rem;
  margin-left: 2.25rem;
`;

const FootNote = styled(Typography)`
  padding: 1rem 7.75rem 0.5rem 2.25rem;
  display: inline-block;
  font-size: 0.875rem;
`;

const BtnBox = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: flex-end;
  padding: 0.5rem 2rem 0;
  margin-bottom: 2rem;
`;

const TableContainer = styled.div`
  margin-top: 2rem;
  height: 20rem;
  overflow: scroll;
`;

const CheckboxWrapper = styled.div`
  max-width: 100%;
  margin: 1rem 2.25rem;
  display: flex;
  align-items: center;
  div {
    margin-right: 1rem;
  }
  span {
    font-size: 0.875rem;
  }
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 1rem 0;
`;

const LoadMoreBtn = styled(Button)`
  padding: 0.2rem 1rem;
`;

const StyledTable = styled(Table)`
  th:first-child,
  td:first-child {
    width: 0.5rem;
  }
  th {
    font-weight: normal;
  }
  td {
    font-size: 14px;
  }
  p {
    font-size: 14px;
  }
  tr:hover {
    background-color: white;
  }
`;

const RemoveButton = styled.button`
  background-color: transparent;
  cursor: pointer;
  border: none;
  color: #dc3545;
  font-weight: 500;
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    margin: 0 10px;
    fill: #dc3545;
  }

  :hover {
    color: #821721;
    svg {
      fill: #821721;
    }
  }
`;

const LoaderContainer = styled.div`
  width: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 90%;
  min-height: 10rem;
`;

const ProjectSendInvitations = ({
  location,
  params,
  surveyUiid,
  organization,
  updateProject,
  credits = 0,
  templateReminder,
  templateEmail,
  history,
  projectId,
  hasDataCollectionStarted,
  closeModal,
  voucherInfo,
}: InviteRepondentProps) => {
  const statusKey: keyof typeof sortOptions = 'status';
  const defaultOrderOption = `-${statusKey}`;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSelectAll, setSelectAll] = useState<string | undefined>(undefined);
  const [respondentsList, setRespondentsList] = useState({
    count: 0,
    results: [],
    next: null,
  });
  const [selectedUiids, manageUiids] = useState<string[]>([]);
  const [parameters, setParams] = useState<Params>({
    ordering: defaultOrderOption,
    limit: '20',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState<string | undefined>(
    undefined
  );
  const t = useFormatMessage();

  const { codename } = params;
  const organizationCodename = codename || organization.codename;

  const sortRespondentsList = (data: any) => {
    return data.sort((el1: any, el2: any) =>
      el1.sent === el2.sent ? 0 : el1.sent ? 1 : -1
    );
  };

  const setCheckboxes = (data: any) => {
    const filteredData = data.results.filter((res: any) => {
      return !res.sent;
    });

    const uuids = [];
    for (const el of filteredData) {
      uuids.push(el.uuid);
    }
    manageUiids(uuids);
  };

  const isVoucher = !!(voucherInfo && voucherInfo.voucher);

  const projectPrice = isVoucher
    ? credits + voucherInfo.claimed_value
    : credits;
  const projectPriceBeforeDiscount = credits;

  useEffect(() => {
    if (surveyUiid) {
      setIsLoaded(false);
      api.resources
        .getRespondentsList({ uuid: surveyUiid, parsedSearch: parameters })
        .then(({ data }: any) => {
          const respondentsListResults = [
            ...respondentsList.results,
            ...data.results,
          ];

          setRespondentsList({
            ...data,
            results: sortRespondentsList(respondentsListResults),
          });
          setCheckboxes(data);
          setIsLoaded(true);
        })
        .catch(() => {
          setIsLoaded(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUiid, location.search, parameters]);

  const sendInvitation = () => {
    const data = {
      send_to_all: !!isSelectAll,
      uuids: selectedUiids,
      invitation_email_template_type: templateEmail.type,
      invitation_template_slug: templateEmail.slug,
      reminder_email_template_type: templateReminder
        ? templateReminder.type
        : undefined,
      reminder_template_slug: templateReminder
        ? templateReminder.slug
        : undefined,
    };
    setIsProcessing(true);
    return api.resources
      .sendSurveysToRespondents({
        orgName: organizationCodename,
        uuid: surveyUiid,
        data,
      })
      .then(() => {
        updateProject();
        setIsProcessing(false);
        showToast({
          type: 'success',
          title: t(
            'project.collectData.sendInitations.success',
            'Emails with invitations to participate in survey have been sent.'
          ),
        });
        history.push(`/project/${projectId}/statistics`);
      })
      .catch(() => {
        setIsProcessing(false);
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const onLoadMoreRespondents = (nextUrl = '') => {
    const nextParsedUrl = new URL(nextUrl);
    const urlParams = queryString.parse(nextParsedUrl.search);

    setParams({ ...params, offset: urlParams.offset as string });
  };

  const toggleSelectAll = () => {
    if (isSelectAll) {
      manageUiids([]);

      return setSelectAll(undefined);
    }

    const allUuids = respondentsList.results.map(
      (respondent: any) => respondent.uuid
    );

    manageUiids(allUuids);

    setSelectAll('true');
  };

  const toggleIsAccepted = () => {
    setIsTermsAccepted(isTermsAccepted ? undefined : 'true');
  };

  const addRemoveUiid = (uuid: string) => {
    const isAlreadyAdded = selectedUiids.includes(uuid);

    const copyUiids = [...selectedUiids];

    if (isAlreadyAdded) {
      const uuidToRemove = copyUiids.indexOf(uuid);
      copyUiids.splice(uuidToRemove, 1);
      setSelectAll(undefined);
      return manageUiids(copyUiids);
    }
    copyUiids.push(uuid);

    manageUiids(copyUiids);
  };

  const removeEmail = (respondentData: any) => {
    const uuid = respondentData.uuid;
    setIsProcessing(true);

    api.resources
      .deleteEmail({ uuid })
      .then(() => {
        const respondentsListResultsFiltered = respondentsList.results.filter(
          (resp: any) => resp.id !== respondentData.id
        );
        const respondentsListCopy = JSON.parse(JSON.stringify(respondentsList));

        respondentsListCopy.results = respondentsListResultsFiltered;
        respondentsListCopy.count = respondentsListResultsFiltered.length;
        setRespondentsList(respondentsListCopy);
        setIsProcessing(false);
      })
      .catch(() => {
        setIsProcessing(false);
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const tableHeaders = [
    <Checkbox
      key="table-selectAll"
      onChange={toggleSelectAll}
      value={isSelectAll}
    />,
    t('project.collectData.sendInvitations.table.email', 'Address'),
    t('project.collectData.sendInvitations.table.status', 'Status'),
    '',
  ];

  const respondents = respondentsList.results;

  return (
    <Grid container spacing={1}>
      <EmptyPlaceholder
        isLoaded={isLoaded}
        withLoader
        hasValues={respondentsList.results.length > 0}
        info={t(
          'project.collectData.sendInvitations.noRespondents',
          'No respondents uploaded.'
        )}
      >
        <Grid item xs={12}>
          <Title variant="copy1">
            {hasDataCollectionStarted
              ? t(
                  'project.collectData.sendInvitations.title.progress',
                  'Add new respondents'
                )
              : t(
                  'project.collectData.sendInvitations.title',
                  'Start data collection'
                )}
          </Title>
          <SubHeader>
            {hasDataCollectionStarted
              ? t(
                  'project.collectData.sendInvitations.description.progress',
                  'Verify the list of respondents before sending survey invitations.'
                )
              : t(
                  'project.collectData.sendInvitations.description',
                  'Choose the emails from the list to send the surveys and accept Terms & Conditions.'
                )}
          </SubHeader>

          <>
            <TableContainer>
              {respondentsList.results && (
                <>
                  <StyledTable>
                    {isProcessing && (
                      <LoaderContainer>
                        <AnimatedPlaceholder
                          isLoaded={!isProcessing}
                          withLoader={true}
                          hasValues={true}
                        />
                      </LoaderContainer>
                    )}
                    <TableHeader cells={tableHeaders} />
                    <TableBody>
                      {respondents.map((respondentData: any) => {
                        const isSend = respondentData.sent;
                        const isChecked = selectedUiids.includes(
                          respondentData.uuid
                        );

                        return (
                          <TableRow key={respondentData.id}>
                            <TableCell>
                              <Checkbox
                                value={isChecked ? 'true' : undefined}
                                onChange={() => {
                                  addRemoveUiid(respondentData.uuid);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {respondentData.respondent.email || ''}
                            </TableCell>
                            <TableCell>
                              <Typography variant="copy1">
                                {isSend
                                  ? t(
                                      'project.collectData.sendInvitations.status.sent',
                                      'sent'
                                    )
                                  : t(
                                      'project.collectData.sendInvitations.status.ready',
                                      'ready to sent'
                                    )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {!respondentData.sent && (
                                <RemoveButton
                                  onClick={() => removeEmail(respondentData)}
                                  data-testid={respondentData.respondent.email}
                                >
                                  <DeleteIcon />
                                  Remove
                                </RemoveButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </StyledTable>

                  {respondentsList.next !== null && (
                    <LoadMoreWrapper>
                      <LoadMoreBtn
                        variant="primary"
                        onClick={() =>
                          onLoadMoreRespondents(respondentsList.next || '')
                        }
                      >
                        {t('project.collectData.sendInvitations.more', 'More')}
                      </LoadMoreBtn>
                    </LoadMoreWrapper>
                  )}
                </>
              )}
            </TableContainer>
            <FootNote>
              {hasDataCollectionStarted
                ? t(
                    'project.collectData.sendInvitations.footNote.progress',
                    'Deeptrue will collect the data from the completed surveys. You will find results in report section on your dashboard.'
                  )
                : t(
                    'project.collectData.sendInvitations.footNote',
                    'Deeptrue will collect the data from the completed surveys. You will find results in report section on your dashboard. The cost of collecting the data is {credits} credits. The cost will be deducted from your balance of credits. By starting data collection you sign a contract with deeptrue, commissioning project execution as defined here.',
                    { credits }
                  )}
            </FootNote>
            {!hasDataCollectionStarted && (
              <CheckboxWrapper>
                <Checkbox onChange={toggleIsAccepted} value={isTermsAccepted} />
                <FormattedHTMLMessage
                  id="project.collectData.sendInvitations.acceptTermsAndConditions"
                  defaultMessage="I accept {link}"
                  values={{
                    link: (
                      <a
                        href={t(
                          'project.collectData.linkModal.termsAndConditions.link',
                          'https://deeptrue.com/de/terms-and-conditions'
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(
                          'project.collectData.linkModal.termsAndConditions.label',
                          'Terms & Conditions'
                        )}
                      </a>
                    ),
                  }}
                />
              </CheckboxWrapper>
            )}
            {!hasDataCollectionStarted && isVoucher && (
              <VoucherInfo
                discountPercent={voucherInfo.voucher.discount_percent}
                projectPrice={projectPrice}
                projectPriceBeforeDiscount={projectPriceBeforeDiscount}
                displayFlex={true}
              />
            )}
            <BtnBox>
              <Button
                variant={'secondary'}
                onClick={closeModal}
                marginRight={'0.5rem'}
              >
                {t('dashboard.template.btn.cancel', 'Cancel')}
              </Button>

              <Button
                disabled={!hasDataCollectionStarted && !isTermsAccepted}
                isLoading={isProcessing}
                onClick={sendInvitation}
              >
                {hasDataCollectionStarted
                  ? t(
                      'project.collectData.sendInvitations.addRespondents',
                      'Add respondents'
                    )
                  : t(
                      'project.collectData.sendInvitations.send',
                      'Start for {credits} Credits',
                      { credits }
                    )}
              </Button>
            </BtnBox>
          </>
        </Grid>
      </EmptyPlaceholder>
    </Grid>
  );
};

export const ProjectSendInvitationsPure = ProjectSendInvitations;

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' })
)(ProjectSendInvitations);
